import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform, useInView, animate } from "framer-motion";
import {
  ArrowRight,
  Play,
  ShieldCheck,
  Users,
  Target,
  CheckCircle2,
  BarChart3,
  Rocket,
  Utensils,
  ShoppingCart,
  ShoppingBag,
  Star,
  Zap,
  ThumbsUp,
  Store,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Reveal, Chapter } from "@/components/Shared";
import ProductMockup from "@/components/ProductMockup";
import Marquee from "@/components/Marquee";
import VideoModal from "@/components/VideoModal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTOR_IMAGES = [
  "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1200&q=80",
];
const SECTOR_SLUGS = ["restauration", "retail-alimentaire", "retail-specialise"];
const SECTOR_ICONS = [Utensils, ShoppingCart, ShoppingBag];
const PILLAR_ICONS = [CheckCircle2, BarChart3, Users, Rocket];
const PILLAR_TILES = [
  "bg-blue-50 text-blue-600",
  "bg-emerald-50 text-emerald-600",
  "bg-violet-50 text-violet-600",
  "bg-amber-50 text-amber-600",
];
const STAT_ICONS = [Zap, ThumbsUp, Store, Star];
const BADGE_ICONS = [ShieldCheck, Users, Target];

const Counter = ({ value, prefix, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 1.9,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value]);
  return (
    <span ref={ref} className="font-mono-orbit">
      {prefix}
      {display}
      {suffix}
    </span>
  );
};

const HomePage = () => {
  const { t } = useLanguage();
  usePageTitle("Pilotage multi-sites restauration & retail", "Multi-site management for hospitality & retail");
  const [videoOpen, setVideoOpen] = useState(false);
  const [sections, setSections] = useState({ stats: false, testimonial: false });
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const mockupY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  useEffect(() => {
    axios
      .get(`${API}/settings/public`)
      .then((res) => setSections(res.data.sections || {}))
      .catch(() => {});
  }, []);

  let chapterCounter = 0;
  const ch = () => String(++chapterCounter).padStart(2, "0");
  const chSolution = ch();
  const chWho = ch();
  const chStats = sections.stats ? ch() : null;
  const chProof = sections.testimonial ? ch() : null;
  const chFaq = ch();

  return (
    <main data-testid="home-page">
      <section
        ref={heroRef}
        data-testid="hero-section"
        className="relative overflow-hidden bg-gradient-to-b from-blue-50/80 via-white to-white pt-[76px]"
      >
        <motion.div
          style={{ y: glowY }}
          className="pointer-events-none absolute -top-40 right-[-10%] h-[560px] w-[560px] rounded-full bg-emerald-400/10 blur-3xl"
        />
        <div className="pointer-events-none absolute inset-0 bg-grid-light opacity-60 [mask-image:radial-gradient(ellipse_at_top,black_20%,transparent_70%)]" />

        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 pb-20 pt-14 lg:grid-cols-[1.05fr_1fr] lg:px-8 lg:pb-28 lg:pt-20">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              data-testid="hero-eyebrow"
              className="inline-block text-[11px] font-bold uppercase tracking-[0.22em] text-emerald-500 sm:text-xs"
            >
              {t.hero.eyebrow}
            </motion.p>

            <h1
              data-testid="hero-title"
              className="mt-5 font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
            >
              {t.hero.titleLines.map((line, i) => (
                <span key={line} className="block overflow-hidden pb-1">
                  <motion.span
                    className={`block ${i === 2 ? "text-blue-600" : ""}`}
                    initial={{ y: "112%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
              data-testid="hero-subtitle"
              className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
            >
              {t.hero.subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/demo"
                data-testid="hero-demo-button"
                className="group flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-blue-600/25 transition-all duration-300 hover:bg-blue-700 hover:shadow-xl hover:shadow-blue-600/30"
              >
                {t.hero.ctaDemo}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <button
                data-testid="hero-video-button"
                onClick={() => setVideoOpen(true)}
                className="group flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-6 py-3.5 text-sm font-bold text-slate-700 transition-colors duration-300 hover:border-slate-300 hover:bg-slate-50"
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 transition-transform duration-300 group-hover:scale-110">
                  <Play className="ml-0.5 h-3 w-3 fill-white text-white" />
                </span>
                {t.hero.ctaVideo}
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="mt-9 flex flex-wrap items-center gap-x-7 gap-y-3"
              data-testid="hero-badges"
            >
              {t.hero.badges.map((badge, i) => {
                const Icon = BADGE_ICONS[i];
                return (
                  <span key={badge} className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-50">
                      <Icon className="h-3.5 w-3.5 text-emerald-500" />
                    </span>
                    {badge}
                  </span>
                );
              })}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 48, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.div style={{ y: mockupY }} className="relative">
              <ProductMockup />
              <motion.p
                initial={{ opacity: 0, rotate: 0 }}
                animate={{ opacity: 1, rotate: -4 }}
                transition={{ duration: 0.7, delay: 1.4 }}
                data-testid="hero-annotation"
                className="font-hand absolute -bottom-8 right-2 text-xl font-semibold text-slate-700 lg:-right-6 lg:text-2xl"
              >
                {t.hero.annotation}
              </motion.p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Marquee />

      <section id="solution" data-testid="pillars-section" className="py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <Chapter index={chSolution} label={t.chapters.solution} />
            <h2 className="max-w-2xl font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              {t.pillars.title}
            </h2>
            <p className="mt-4 max-w-xl text-base text-slate-600 sm:text-lg">{t.pillars.subtitle}</p>
          </Reveal>
          <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {t.pillars.items.map((pillar, i) => {
              const Icon = PILLAR_ICONS[i];
              return (
                <Reveal key={pillar.title} delay={i * 0.1}>
                  <div
                    data-testid={`pillar-card-${i}`}
                    className="group h-full rounded-2xl border border-slate-100 bg-white p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-slate-200 hover:shadow-[0_24px_48px_-16px_rgba(10,15,29,0.12)]"
                  >
                    <span
                      className={`flex h-12 w-12 items-center justify-center rounded-xl transition-transform duration-500 group-hover:scale-110 ${PILLAR_TILES[i]}`}
                    >
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-5 font-display text-lg font-bold text-slate-900">{pillar.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{pillar.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section id="secteurs" data-testid="sectors-section" className="bg-slate-50/70 py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="text-center">
            <div className="flex justify-center">
              <Chapter index={chWho} label={t.chapters.who} />
            </div>
            <h2 className="mx-auto max-w-2xl font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl text-balance">
              {t.sectors.title}
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-slate-600 sm:text-lg">{t.sectors.subtitle}</p>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {t.sectors.items.map((sector, i) => {
              const Icon = SECTOR_ICONS[i];
              return (
                <Reveal key={sector.title} delay={i * 0.12}>
                  <div
                    data-testid={`sector-card-${i}`}
                    className="group relative flex h-96 flex-col justify-end overflow-hidden rounded-2xl"
                  >
                    <img
                      src={SECTOR_IMAGES[i]}
                      alt={sector.title}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1D]/95 via-[#0A0F1D]/40 to-transparent" />
                    <div className="relative p-7">
                      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 backdrop-blur-md">
                        <Icon className="h-5 w-5 text-white" />
                      </span>
                      <h3 className="mt-4 font-display text-xl font-bold text-white">{sector.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate-200">{sector.text}</p>
                      <Link
                        to={`/secteurs/${SECTOR_SLUGS[i]}`}
                        data-testid={`sector-more-link-${i}`}
                        className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-emerald-400 transition-colors duration-200 hover:text-emerald-300"
                      >
                        {t.sectors.more}
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </Link>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {sections.stats && (
        <section data-testid="stats-section" className="relative overflow-hidden bg-[#0A0F1D] py-20 lg:py-28">
          <div className="pointer-events-none absolute inset-0 bg-grid-navy" />
          <div className="pointer-events-none absolute -left-32 top-0 h-96 w-96 rounded-full bg-emerald-500/10 blur-3xl" />
          <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
            <div className="grid items-center gap-12 lg:grid-cols-[0.9fr_2fr]">
              <Reveal>
                <Chapter index={chStats} label={t.chapters.results} dark />
                <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl text-balance">
                  {t.stats.title}
                </h2>
                <p className="mt-4 text-sm text-slate-400 sm:text-base">{t.stats.subtitle}</p>
              </Reveal>
              <div className="grid grid-cols-2 gap-x-8 gap-y-12 lg:grid-cols-4">
                {t.stats.items.map((stat, i) => {
                  const Icon = STAT_ICONS[i];
                  return (
                    <Reveal key={stat.label} delay={i * 0.1}>
                      <div data-testid={`stat-item-${i}`}>
                        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/5 ring-1 ring-white/10">
                          <Icon className="h-5 w-5 text-emerald-400" />
                        </span>
                        <p className="mt-4 font-display text-4xl font-extrabold text-white lg:text-5xl">
                          <Counter value={stat.value} prefix={stat.prefix} suffix={stat.suffix} />
                        </p>
                        <p className="mt-2 text-sm leading-snug text-slate-400">{stat.label}</p>
                      </div>
                    </Reveal>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {sections.testimonial && (
        <section data-testid="testimonial-section" className="py-20 lg:py-32">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <Reveal>
              <Chapter index={chProof} label={t.chapters.proof} />
            </Reveal>
            <div className="grid gap-6 lg:grid-cols-2">
              <Reveal>
                <div
                  data-testid="testimonial-card"
                  className="flex h-full flex-col justify-between rounded-2xl border border-slate-100 bg-white p-9 shadow-[0_16px_40px_-16px_rgba(10,15,29,0.08)]"
                >
                  <div>
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    <blockquote className="mt-6 font-display text-xl font-medium leading-relaxed text-slate-800 lg:text-2xl text-balance">
                      “{t.testimonial.quote}”
                    </blockquote>
                  </div>
                  <div className="mt-8 flex items-center gap-4">
                    <img
                      src="https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjByZXRhaWwlMjBkaXJlY3RvciUyMHBvcnRyYWl0JTIwZXhlY3V0aXZlfGVufDB8fHx8MTc4ODkyMDEyMHww&ixlib=rb-4.1.0&q=85"
                      alt={t.testimonial.name}
                      loading="lazy"
                      className="h-12 w-12 rounded-full object-cover ring-2 ring-emerald-100"
                    />
                    <div>
                      <p className="text-sm font-bold text-slate-900">{t.testimonial.name}</p>
                      <p className="text-xs text-slate-500">{t.testimonial.role}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.12}>
                <div
                  data-testid="cta-card"
                  className="relative flex h-full flex-col justify-center overflow-hidden rounded-2xl bg-[#0A0F1D] p-9 lg:p-12"
                >
                  <div className="pointer-events-none absolute inset-0 bg-grid-navy" />
                  <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-emerald-500/15 blur-3xl" />
                  <div className="relative">
                    <h3 className="font-display text-2xl font-bold tracking-tight text-white lg:text-3xl text-balance">
                      {t.testimonial.ctaTitle}
                    </h3>
                    <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-400">{t.testimonial.ctaText}</p>
                    <Link
                      to="/demo"
                      data-testid="cta-demo-button"
                      className="group mt-7 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-blue-500"
                    >
                      {t.testimonial.ctaButton}
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      <section id="faq" data-testid="faq-section" className="bg-slate-50/70 py-20 lg:py-32">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <Reveal>
            <Chapter index={chFaq} label={t.chapters.resources} />
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              {t.faq.title}
            </h2>
            <p className="mt-4 text-base text-slate-600 sm:text-lg">{t.faq.subtitle}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
              {t.faq.items.map((item, i) => (
                <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`} className="border-slate-200">
                  <AccordionTrigger
                    data-testid={`faq-trigger-${i}`}
                    className="text-left font-display text-base font-bold text-slate-900 hover:no-underline"
                  >
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-slate-600">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <VideoModal open={videoOpen} onOpenChange={setVideoOpen} title={t.hero.videoTitle} testid="video-modal" />
    </main>
  );
};

export default HomePage;
